import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Divider } from '@react-spectrum/divider';
import Copy from '@spectrum-icons/workflow/Copy';
import styled from 'styled-components';
import { WorkflowApp } from 'api/wfApi';
import { Breadcrumb, Breadcrumbs } from 'components/basic/breadcrumbs';
import { Button } from 'components/basic/button';
import { CopyTextButton } from 'components/basic/copyTextButton';
import { Loading } from 'components/basic/loading';
import { Overlay } from 'components/basic/overlay';
import {
  AnnotationVariant,
  Annotation,
} from 'components/parts/common/annotation';
import { UpdateExecuteKeyButton } from 'components/parts/updateExecuteKeyButton';
import { LogList } from 'components/parts/logList';
import { useWorkflowAppsContext } from 'components/providers/workflowAppsContext';
import { SETTINGS_ANNOTATION } from 'constants/constants';
import { useSettings } from 'feature/settings';
import urls from 'urls';

const SettingsLayout = styled.div`
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
  padding: 10px 10px 0;
  position: relative;
  width: 100vw;
`;
const Usage = styled.div`
  display: flex;
  padding: 10px 150px;
  justify-content: space-between;
`;
const UsageDetail = styled.div`
  align-items: center;
  display: flex;
  padding-left: 10px;
`;
const UsageDetailText = styled.div`
  display: flex;
  margin-right: 10px;
`;
const CopyButtonArea = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0px 10px;
  width: 150px;
`;
const UsageHeader = styled.div`
  padding: 0px 20px;
`;
const Headline = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`;
const ButtonArea = styled.div`
  align-items: start;
  display: flex;
  justify-content: center;
  margin: 10px 20px;
`;

export const Settings = () => {
  const workflowAppsContext = useWorkflowAppsContext();
  const [getSettings, updateExecuteKey, executeKey, loading] = useSettings();
  const navigate = useNavigate();
  const { wfId } = useParams<{ wfId: string }>();
  const [workflowApp, setWorkflowApp] = useState<WorkflowApp>();
  const [annotationVariant, setAnnotationVariant] =
    useState<AnnotationVariant>('');
  const [annotationMessage, setAnnotationMessage] = useState<string>('');

  // パンくずリスト
  const breadcrumbList: Breadcrumb[] = [
    {
      name: '利用アプリ一覧',
      path: urls.WORKFLOW_LIST,
      key: 'workflowList',
    },
    {
      name: workflowApp ? workflowApp.name : '',
      key: wfId?.toString() ? wfId.toString() : '',
    },
  ];

  // wfId から WF 情報取得する
  const getWorkflowApps = useCallback(async () => {
    // リンク指定時は WF アプリを取得できていないので WF アプリ取得する
    if (workflowAppsContext.workflowApps.length === 0)
      await workflowAppsContext.getWorkflowApps();
  }, [workflowAppsContext]);

  // 設定情報取得
  const getExecuteSettings = useCallback(async () => {
    const workflowAppInfo = workflowAppsContext.workflowApps.find(
      (workflowApp) => Number(workflowApp.id) === Number(wfId),
    );
    // 一致するwfIdがなくworkflowAppInfoがundefinedの場合
    // executeKeyを発行させないためにreturnする
    if (!workflowAppInfo) return
    const getResult = await getSettings(
      Number(wfId),
      String(workflowAppInfo?.serviceClass),
      workflowAppInfo?.appKey,
    );
    setAnnotationVariant(getResult ? getResult.VARIANT : '');
    setAnnotationMessage(getResult ? getResult.MESSAGE : '');
  }, [getSettings, wfId, workflowAppsContext]);

  // アプリ一覧画面に遷移
  const returnWorkflowListButton = useCallback(() => {
    navigate(urls.WORKFLOW_LIST);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getWorkflowApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getExecuteSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowAppsContext]);

  // 指定した wfId で WF アプリを取得する
  useEffect(() => {
    if (workflowAppsContext.loading) {
      setAnnotationVariant('');
      setAnnotationMessage('');
      return;
    }

    const workflowApp = workflowAppsContext.workflowApps.find(
      (workflowApp) => wfId === workflowApp.id.toString(),
    );

    if (workflowApp) {
      setWorkflowApp(workflowApp);
    } else {
      // WF アプリがない場合
      setAnnotationVariant(SETTINGS_ANNOTATION.WF_APP_NOT_FOUND.VARIANT);
      setAnnotationMessage(SETTINGS_ANNOTATION.WF_APP_NOT_FOUND.MESSAGE);
    }
  }, [wfId, workflowAppsContext]);

  return (
    <SettingsLayout>
      {(workflowAppsContext.loading || loading) && (
        <Overlay>
          <Loading />
        </Overlay>
      )}
      <Annotation message={annotationMessage} variant={annotationVariant} />
      <Breadcrumbs breadcrumbList={breadcrumbList} />
      <Usage>
        <div>
          <Headline>使用方法</Headline>
          <UsageDetail>連携先URL:</UsageDetail>
          <UsageHeader>
            <UsageDetail>
              <UsageDetailText>
                POST {`${process.env.REACT_APP_ENDPOINT}/execute`}
              </UsageDetailText>
              <CopyButtonArea>
                <CopyTextButton
                  text={`${process.env.REACT_APP_ENDPOINT}/execute`}
                  margin={0}
                >
                  <Copy />
                </CopyTextButton>
              </CopyButtonArea>
            </UsageDetail>
          </UsageHeader>
          <UsageDetail>リクエストヘッダー:</UsageDetail>
          <UsageHeader>
            <UsageDetail>
              <UsageDetailText>execute-Key: {executeKey}</UsageDetailText>
              {executeKey && (
                <CopyButtonArea>
                  <CopyTextButton text={executeKey} margin={0}>
                    <Copy />
                  </CopyTextButton>
                  <UpdateExecuteKeyButton
                    updateExecuteKey={updateExecuteKey}
                    setAnnotationVariant={setAnnotationVariant}
                    setAnnotationMessage={setAnnotationMessage}
                  />
                </CopyButtonArea>
              )}
            </UsageDetail>
          </UsageHeader>
        </div>
        <ButtonArea>
          <Button onClick={returnWorkflowListButton}>戻る</Button>
        </ButtonArea>
      </Usage>
      <Divider size="M" />
      <LogList wfId={Number(wfId)} />
    </SettingsLayout>
  );
};
